/**
 * Reads the parts of a recognition result the planner uses, defensively: the result is the
 * engine's JSON passed through untyped (see PackagePage.recognition), so any field may be
 * absent or malformed on an older result.
 */

import type { PlanPoint } from "./types.ts";

export interface Segment {
  readonly a: PlanPoint;
  readonly b: PlanPoint;
}

export interface Tag {
  readonly position: PlanPoint;
  readonly label: string;
}

export interface PlanInput {
  /** Wall linework, model inches, Y up. */
  readonly walls: readonly Segment[];
  /** Lines across door openings, so a room's flood fill stops at its door. */
  readonly doorSeals: readonly Segment[];
  readonly tags: readonly Tag[];
}

type Json = Record<string, unknown>;

const isObject = (value: unknown): value is Json =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

function point(value: unknown, scale: number): PlanPoint | null {
  if (!isObject(value)) return null;
  const { x, y } = value;
  if (typeof x !== "number" || typeof y !== "number") return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x: x * scale, y: -y * scale };
}

function chain(points: readonly (PlanPoint | null)[], closed: boolean, out: Segment[]): void {
  const valid = points.filter((p): p is PlanPoint => p !== null);
  for (let i = 1; i < valid.length; i++) out.push({ a: valid[i - 1]!, b: valid[i]! });
  if (closed && valid.length > 2) out.push({ a: valid[valid.length - 1]!, b: valid[0]! });
}

export function readRecognition(recognition: unknown, inchesPerPreviewPixel: number): PlanInput {
  const s = inchesPerPreviewPixel;
  const walls: Segment[] = [];
  const doorSeals: Segment[] = [];
  const tags: Tag[] = [];
  if (!isObject(recognition) || !(s > 0) || !Number.isFinite(s)) return { walls, doorSeals, tags };

  for (const line of list(recognition["wallLines"])) {
    if (!isObject(line)) continue;
    chain([point(line["start"], s), point(line["end"], s)], false, walls);
  }
  for (const line of list(recognition["polylines"])) {
    if (!isObject(line)) continue;
    chain(list(line["vertices"]).map((v) => point(v, s)), line["closed"] === true, walls);
  }
  for (const spline of list(recognition["splines"])) {
    if (!isObject(spline)) continue;
    chain(list(spline["fitPoints"]).map((v) => point(v, s)), false, walls);
  }
  for (const arc of list(recognition["arcLines"])) {
    if (!isObject(arc) || arc["arcType"] === "DoorSwing" || arc["arcType"] === undefined) continue;
    const center = point(arc["center"], 1);
    const radius = arc["radius"];
    const start = arc["startAngleRadians"];
    const sweep = arc["sweepRadians"];
    if (center === null || typeof radius !== "number" || typeof start !== "number") continue;
    if (typeof sweep !== "number" || !Number.isFinite(sweep) || sweep === 0) continue;
    const steps = Math.max(2, Math.ceil(Math.abs(sweep) / (Math.PI / 16)));
    const points: PlanPoint[] = [];
    for (let i = 0; i <= steps; i++) {
      const angle = start + (sweep * i) / steps;
      // Preview space is Y down, so the angle is measured there before the flip.
      points.push({
        x: (center.x + radius * Math.cos(angle)) * s,
        y: -(-center.y + radius * Math.sin(angle)) * s,
      });
    }
    chain(points, false, walls);
  }

  for (const door of list(recognition["doors"])) {
    if (!isObject(door)) continue;
    const swings = list(door["swings"]);
    if (swings.length > 0) {
      for (const swing of swings) {
        if (!isObject(swing)) continue;
        chain([point(swing["hinge"], s), point(swing["closedTip"], s)], false, doorSeals);
      }
      continue;
    }
    const center = point(door["center"], s);
    const width = door["widthInches"];
    const rotation = door["rotationDegrees"];
    if (center === null || typeof width !== "number" || !(width > 0)) continue;
    const radians = ((typeof rotation === "number" ? rotation : 0) * Math.PI) / 180;
    const dx = (Math.cos(radians) * width) / 2;
    const dy = (-Math.sin(radians) * width) / 2;
    doorSeals.push({ a: { x: center.x - dx, y: center.y - dy }, b: { x: center.x + dx, y: center.y + dy } });
  }

  for (const tag of list(recognition["roomTags"])) {
    if (!isObject(tag)) continue;
    const position = point(tag["position"], s);
    if (position === null) continue;
    const number = typeof tag["roomNumber"] === "string" ? tag["roomNumber"] : "";
    const name = typeof tag["roomName1"] === "string" ? tag["roomName1"] : "";
    tags.push({ position, label: number || name });
  }

  if (tags.length === 0) tags.push(...tagsFromText(recognition, s, walls));

  return { walls, doorSeals, tags };
}

/** Matches RoomNumberNormalizer.RoomNumberPattern, which the connector's recovery uses too. */
const ROOM_NUMBER = /^(?:[A-Za-z]?\d{1,4}[A-Za-z]?|[A-Za-z]?\d{1,2}-\d{2,4}[A-Za-z]?)$/;

/**
 * The connector rebuilds room tags from OCR text when a result has none
 * (ConnectorCommands.RecoverRoomTagsFromText), and draws them. Seeding rooms from the same text
 * keeps the plan from cutting through a room the drawing will show tagged. Only positions
 * matter here, so the name matching and leader detection the connector also does are left out.
 */
function tagsFromText(recognition: Json, scale: number, walls: readonly Segment[]): Tag[] {
  if (walls.length === 0) return [];
  const xs = walls.flatMap(({ a, b }) => [a.x, b.x]);
  const ys = walls.flatMap(({ a, b }) => [a.y, b.y]);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const padX = Math.max(20 * scale, (maxX - minX) * 0.03);
  const padY = Math.max(20 * scale, (maxY - minY) * 0.03);
  const out: Tag[] = [];
  for (const region of list(recognition["textRegions"])) {
    if (!isObject(region)) continue;
    const { left, top, right, bottom, text } = region;
    if (typeof text !== "string" || !ROOM_NUMBER.test(text.trim().replace(/\s+/g, " "))) continue;
    if (typeof left !== "number" || typeof top !== "number" || typeof right !== "number" || typeof bottom !== "number") {
      continue;
    }
    const position = point({ x: (left + right) / 2, y: (top + bottom) / 2 }, scale);
    if (position === null) continue;
    if (position.x < minX - padX || position.x > maxX + padX || position.y < minY - padY || position.y > maxY + padY) {
      continue;
    }
    out.push({ position, label: text.trim() });
  }
  return out;
}
